<script lang="ts">
	//Svelte hooks
	import { onMount, beforeUpdate } from 'svelte';
	//Services
	import { postReq } from '../../../services/http';
	import { format } from '../../../services/i18n';
	//Stores
	import { getDeviceStatus, getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import { devices } from 'src/stores/lists/devices';
	import { os, app } from 'src/stores/session/native-bridge';
	import { real_id } from '../../../helpers/interface_helpers';
	import { loqedPendingOperationsDevices } from 'src/stores/integrations/loqed';
	import { pin_unlocked_devices } from 'src/stores/device-runtime/pin-unlocked';
	//Helpers
	import { loqedToShelly } from 'src/helpers/loqed_helpers';
	import { hapticFeedback } from 'src/services/native_container_requests_ios';
	import { unlockPin } from 'src/helpers/pin_lock_helpers';
	//Design system components
	import {
		DeviceButton,
		TripleDeviceButton,
		DoubleDeviceButton,
		toast,
		ControlsContainer,
		Text,
		TripleDeviceButtonV2,
		DeviceButtonV2,
		DoubleDeviceButtonV2,
		type DeviceButtonV2PropsTypes,
		type TripleDeviceButtonV2PropsTypes,
		type DoubleDeviceButtonV2PropsTypes,
	} from '@Shelly/shelly-design-system';
	//App components
	import PinUnlock from 'src/components/PinUnlock.svelte';
	import { hasControlRights } from 'src/helpers/user_helpers';

	export let device,
		isWide = false;
	export let useV2Components = false;
	export let devBtnsV2Props: {
		deviceButtonV2?: Partial<DeviceButtonV2PropsTypes>;
		doubleDeviceButtonV2?: Partial<DoubleDeviceButtonV2PropsTypes>;
		tripleDeviceButtonV2?: Partial<TripleDeviceButtonV2PropsTypes>;
	} = {
		deviceButtonV2: { size: 's' },
		doubleDeviceButtonV2: { size: 's' },
		tripleDeviceButtonV2: { size: 's' },
	};

	$: statusStore = getDeviceStatus(device.id);
	$: status = $statusStore;
	$: isLoading = typeof $loqedPendingOperationsDevices[device.id] !== 'undefined';
	$: pendingCommand = $loqedPendingOperationsDevices[device.id];

	let supportedStates: string[] = [];
	let isPinLocked: boolean = false;
	let showPinLockModal: boolean = false;
	let callbackValue: string; // Store expected state (command) to be forwarded if we have to pin-unlock

	$: hasRights = hasControlRights(device);

	const renderDataByState = {
		open: {
			defaultIcon: 'fas fa-door-open',
			defaultText: $format(`label_action_bolt_state_open`),
			defaultCommand: 'open',
			icon: 'fas fa-lock',
			text: $format(`label_action_bolt_state_night_lock`),
			command: 'locked',
		},
		day_lock: {
			icon: 'fas fa-lock',
			text: $format(`label_action_bolt_state_night_lock`),
			command: 'locked',
		},
		night_lock: {
			icon: 'fas fa-unlock',
			text: $format(`label_action_bolt_state_day_lock`),
			command: 'unlocked',
		},
	};

	const a11yLabels = {
		open: $format('open_door', { default: 'Open door' }),
		lock: $format('lock_door', { default: 'Lock door' }),
		unlock: $format('unlock_door', { default: 'Unlock door' }),
		doorControl: $format('door_control', { default: 'Door control' }),
	};

	onMount(() => {
		status = $statusStore;

		supportedStates =
			typeof status?.supported_lock_states !== 'undefined' ? status.supported_lock_states : [];
	});

	beforeUpdate(() => {
		isPinLocked =
			typeof $pin_unlocked_devices[device.id] === 'undefined' && $devices[device.id]?.pin_lock;
	});

	const handleLock = async (_expectedState) => {
		if (!isPinLocked) {
			if ($app) {
				if ($os === 'IOS') {
					hapticFeedback('buttonPressed');
				}
			}

			// If the expected state is the same as the current state, return
			if (_expectedState === loqedToShelly(status.bolt_state)) {
				return;
			}

			// Add to pending operations store to track state
			if (!isLoading) {
				loqedPendingOperationsDevices.add(device.id, _expectedState);
			} else {
				return;
			}

			const res = await postReq(
				`v2/devices/set/lock`,
				{
					id: device.id,
					state: _expectedState,
				},
				10000,
			);

			setTimeout(() => {
				if (isLoading) {
					loqedPendingOperationsDevices.remove(device.id);
				}
			}, 20000);

			if (res?.error) {
				toast.open({
					type: 'danger',
					title: $format('error_global'),
				});
			}
		} else {
			callbackValue = _expectedState;
			showPinLockModal = true;
		}
	};

	const handlePin = () => {
		isPinLocked = unlockPin(device, isPinLocked);
		handleLock(callbackValue);
		callbackValue = undefined;
	};
</script>

{#if showPinLockModal}
	<PinUnlock
		bind:showModal={showPinLockModal}
		on:unlock={handlePin}
		on:cancel={() => (showPinLockModal = false)}
	/>
{/if}

<!-- On unknown state, show all available buttons -->
{#if status?.bolt_state === 'unknown'}
	<!-- 3 supported states -->
	{#if supportedStates.length == 3}
		{#if !isWide}
			{#if useV2Components}
				<TripleDeviceButtonV2
					isActive={isLoading}
					{isLoading}
					disabled={isLoading || !hasRights}
					aria-label={a11yLabels.doorControl}
					{...devBtnsV2Props.tripleDeviceButtonV2}
				>
					<DeviceButtonV2
						id="devicebutton-j75dbx3"
						slot="buttonLeft"
						icon={renderDataByState['open'].defaultIcon}
						aria-label={a11yLabels.open}
						{...devBtnsV2Props.deviceButtonV2}
						on:click={(e) => {
							e.stopPropagation();
							handleLock(renderDataByState['open'].defaultCommand);
						}}
					/>
					<DeviceButtonV2
						id="devicebutton-wj5c8x3"
						slot="buttonMiddle"
						icon={renderDataByState['night_lock'].icon}
						aria-label={a11yLabels.unlock}
						{...devBtnsV2Props.deviceButtonV2}
						on:click={(e) => {
							e.stopPropagation();
							handleLock(renderDataByState['night_lock'].command);
						}}
					/>
					<DeviceButtonV2
						id="devicebutton-wtj1zaa"
						slot="buttonRight"
						icon={renderDataByState['day_lock'].icon}
						aria-label={a11yLabels.lock}
						{...devBtnsV2Props.deviceButtonV2}
						on:click={(e) => {
							e.stopPropagation();
							handleLock(renderDataByState['day_lock'].command);
						}}
					/>
				</TripleDeviceButtonV2>
			{:else}
				<TripleDeviceButton
					isActive={isLoading}
					{isLoading}
					disabled={isLoading || !hasRights}
					aria-label={a11yLabels.doorControl}
				>
					<DeviceButton
						id="devicebutton-j75dbx3"
						slot="buttonLeft"
						icon={renderDataByState['open'].defaultIcon}
						aria-label={a11yLabels.open}
						on:click={(e) => {
							e.stopPropagation();
							handleLock(renderDataByState['open'].defaultCommand);
						}}
					/>
					<DeviceButton
						id="devicebutton-wj5c8x3"
						slot="buttonMiddle"
						icon={renderDataByState['night_lock'].icon}
						aria-label={a11yLabels.unlock}
						on:click={(e) => {
							e.stopPropagation();
							handleLock(renderDataByState['night_lock'].command);
						}}
					/>
					<DeviceButton
						id="devicebutton-wtj1zaa"
						slot="buttonRight"
						icon={renderDataByState['day_lock'].icon}
						aria-label={a11yLabels.lock}
						on:click={(e) => {
							e.stopPropagation();
							handleLock(renderDataByState['day_lock'].command);
						}}
					/>
				</TripleDeviceButton>
			{/if}
		{:else}
			<!-- For DeviceDrawer -->
			<ControlsContainer size="s" color="color-0">
				<svelte:fragment slot="first">
					<DeviceButton
						id="devicebutton-njdkkf7"
						icon={renderDataByState['open'].defaultIcon}
						isActive={isLoading && pendingCommand === 'open'}
						isLoading={isLoading && pendingCommand === 'open'}
						disabled={isLoading || !hasRights}
						size="m"
						aria-label={a11yLabels.open}
						on:click={(e) => {
							handleLock(renderDataByState['open'].defaultCommand);
						}}
					/>
					{#if isWide}
						<Text styleType={'controls-small'} class="mt-s text-bold"
							>{renderDataByState['open'].defaultText}</Text
						>
					{/if}
				</svelte:fragment>
				<svelte:fragment slot="second">
					<DeviceButton
						id="devicebutton-jrpn2yb"
						icon={renderDataByState['night_lock'].icon}
						isActive={isLoading && pendingCommand === 'unlocked'}
						isLoading={isLoading && pendingCommand === 'unlocked'}
						disabled={isLoading || !hasRights}
						size="m"
						aria-label={a11yLabels.unlock}
						on:click={(e) => {
							handleLock(renderDataByState['night_lock'].command);
						}}
					/>
					{#if isWide}
						<Text styleType={'controls-small'} class="mt-s text-bold"
							>{renderDataByState['night_lock'].text}</Text
						>
					{/if}
				</svelte:fragment>
				<svelte:fragment slot="third">
					<DeviceButton
						id="devicebutton-izaobul"
						icon={renderDataByState['day_lock'].icon}
						isActive={isLoading && pendingCommand === 'locked'}
						isLoading={isLoading && pendingCommand === 'locked'}
						disabled={isLoading || !hasRights}
						size="m"
						aria-label={a11yLabels.lock}
						on:click={(e) => {
							handleLock(renderDataByState['day_lock'].command);
						}}
					/>
					{#if isWide}
						<Text styleType={'controls-small'} class="mt-s text-bold"
							>{renderDataByState['day_lock'].text}</Text
						>
					{/if}
				</svelte:fragment>
			</ControlsContainer>
		{/if}
	{:else if supportedStates.length == 2}
		{#if !isWide}
			{#if useV2Components}
				<DoubleDeviceButtonV2
					id="doubledevicebutton-191pa1q"
					isActive={isLoading}
					{isLoading}
					disabled={isLoading || !hasRights}
					aria-label={a11yLabels.doorControl}
					{...devBtnsV2Props.doubleDeviceButtonV2}
				>
					<DeviceButtonV2
						id="devicebutton-a7edw2g"
						slot="buttonLeft"
						icon={renderDataByState['night_lock'].icon}
						aria-label={a11yLabels.unlock}
						on:click={(e) => {
							e.stopPropagation();
							handleLock(renderDataByState['night_lock'].command);
						}}
						{...devBtnsV2Props.deviceButtonV2}
					/>
					<DeviceButtonV2
						id="devicebutton-6mx1epy"
						slot="buttonRight"
						icon={renderDataByState['day_lock'].icon}
						aria-label={a11yLabels.lock}
						on:click={(e) => {
							e.stopPropagation();
							handleLock(renderDataByState['day_lock'].command);
						}}
						{...devBtnsV2Props.deviceButtonV2}
					/>
				</DoubleDeviceButtonV2>
			{:else}
				<DoubleDeviceButton
					id="doubledevicebutton-191pa1q"
					isActive={isLoading}
					{isLoading}
					disabled={isLoading || !hasRights}
					aria-label={a11yLabels.doorControl}
				>
					<DeviceButton
						id="devicebutton-a7edw2g"
						slot="buttonLeft"
						icon={renderDataByState['night_lock'].icon}
						aria-label={a11yLabels.unlock}
						on:click={(e) => {
							e.stopPropagation();
							handleLock(renderDataByState['night_lock'].command);
						}}
					/>
					<DeviceButton
						id="devicebutton-6mx1epy"
						slot="buttonRight"
						icon={renderDataByState['day_lock'].icon}
						aria-label={a11yLabels.lock}
						on:click={(e) => {
							e.stopPropagation();
							handleLock(renderDataByState['day_lock'].command);
						}}
					/>
				</DoubleDeviceButton>
			{/if}
		{:else}
			<!-- For DeviceDrawer -->
			<ControlsContainer color="color-0">
				<svelte:fragment slot="first">
					<DeviceButton
						id="devicebutton-oit1kxf"
						aria-label={a11yLabels.lock}
						icon={renderDataByState['night_lock'].icon}
						isActive={isLoading && pendingCommand === 'locked'}
						isLoading={isLoading && pendingCommand === 'locked'}
						disabled={isLoading || !hasRights}
						size="l"
						on:click={(e) => {
							handleLock(renderDataByState['night_lock'].command);
						}}
					/>
					{#if isWide}
						<Text styleType={'controls-large'} class="mt-s text-bold"
							>{renderDataByState['night_lock'].text}</Text
						>
					{/if}
				</svelte:fragment>
				<svelte:fragment slot="second">
					<DeviceButton
						id="devicebutton-cnee1fs"
						aria-label={a11yLabels.unlock}
						icon={renderDataByState['day_lock'].icon}
						isActive={isLoading && pendingCommand === 'unlocked'}
						isLoading={isLoading && pendingCommand === 'unlocked'}
						disabled={isLoading || !hasRights}
						size="l"
						on:click={(e) => {
							handleLock(renderDataByState['day_lock'].command);
						}}
					/>
					{#if isWide}
						<Text styleType={'controls-large'} class="mt-s text-bold"
							>{renderDataByState['day_lock'].text}</Text
						>
					{/if}
				</svelte:fragment>
			</ControlsContainer>
		{/if}
	{:else if supportedStates.length == 1}
		{#if isWide}
			<ControlsContainer color="color-0">
				<svelte:fragment slot="first">
					<DeviceButton
						id="devicebutton-a44y7hk"
						{isWide}
						isActive={isLoading}
						{isLoading}
						disabled={isLoading || !hasRights}
						size="l"
						icon={renderDataByState[supportedStates[0]].icon}
						aria-label={supportedStates[0] === 'night_lock'
							? a11yLabels.unlock
							: a11yLabels.lock}
						on:click={(e) => {
							e.stopPropagation();
							handleLock(renderDataByState[supportedStates[0]].command);
						}}
					/>
					<Text styleType="controls-large" class="mt-s text-bold"
						>{renderDataByState[supportedStates[0]].text}</Text
					>
				</svelte:fragment>
			</ControlsContainer>
		{:else if useV2Components}
			<DeviceButtonV2
				id="devicebutton-63zhln7"
				{...devBtnsV2Props.deviceButtonV2}
				isActive={isLoading}
				{isLoading}
				disabled={isLoading || !hasRights}
				icon={renderDataByState[supportedStates[0]].icon}
				aria-label={supportedStates[0] === 'night_lock' ? a11yLabels.unlock : a11yLabels.lock}
				on:click={(e) => {
					e.stopPropagation();
					handleLock(renderDataByState[supportedStates[0]].command);
				}}
			/>
		{:else}
			<DeviceButton
				id="devicebutton-63zhln7"
				{isWide}
				isActive={isLoading}
				{isLoading}
				disabled={isLoading || !hasRights}
				icon={renderDataByState[supportedStates[0]].icon}
				aria-label={supportedStates[0] === 'night_lock' ? a11yLabels.unlock : a11yLabels.lock}
				on:click={(e) => {
					e.stopPropagation();
					handleLock(renderDataByState[supportedStates[0]].command);
				}}
			/>
		{/if}
	{/if}
{:else if supportedStates.length == 2}
	{#if isWide}
		<ControlsContainer color="color-0">
			<svelte:fragment slot="first">
				<DeviceButton
					id="devicebutton-bxl4zew"
					{isWide}
					isActive={isLoading}
					{isLoading}
					disabled={isLoading || !hasRights}
					size="l"
					icon={renderDataByState[status?.bolt_state].icon}
					aria-label={status?.bolt_state === 'night_lock'
						? a11yLabels.unlock
						: a11yLabels.lock}
					on:click={(e) => {
						e.stopPropagation();
						handleLock(renderDataByState[status?.bolt_state].command);
					}}
				/>
				<Text styleType="controls-large" class="mt-s text-bold"
					>{renderDataByState[status?.bolt_state].text}</Text
				>
			</svelte:fragment>
		</ControlsContainer>
	{:else if useV2Components}
		<DeviceButtonV2
			id="devicebutton-vrs0teu"
			{...devBtnsV2Props.deviceButtonV2}
			isActive={isLoading}
			{isLoading}
			disabled={isLoading || !hasRights}
			icon={renderDataByState[status?.bolt_state].icon}
			aria-label={status?.bolt_state === 'night_lock' ? a11yLabels.unlock : a11yLabels.lock}
			on:click={(e) => {
				e.stopPropagation();
				handleLock(renderDataByState[status?.bolt_state].command);
			}}
		/>
	{:else}
		<DeviceButton
			id="devicebutton-vrs0teu"
			{isWide}
			isActive={isLoading}
			{isLoading}
			disabled={isLoading || !hasRights}
			icon={renderDataByState[status?.bolt_state].icon}
			aria-label={status?.bolt_state === 'night_lock' ? a11yLabels.unlock : a11yLabels.lock}
			on:click={(e) => {
				e.stopPropagation();
				handleLock(renderDataByState[status?.bolt_state].command);
			}}
		/>
	{/if}
{:else if supportedStates.length == 3}
	{#if !isWide}
		{#if useV2Components}
			<DoubleDeviceButtonV2
				id="doubledevicebutton-2hjwupo"
				isActive={isLoading}
				{isLoading}
				disabled={isLoading || !hasRights}
				aria-label={a11yLabels.doorControl}
				{...devBtnsV2Props.doubleDeviceButtonV2}
			>
				<DeviceButtonV2
					id="devicebutton-w9mg8oa"
					slot="buttonLeft"
					icon={renderDataByState['open'].defaultIcon}
					aria-label={a11yLabels.open}
					on:click={(e) => {
						e.stopPropagation();
						handleLock(renderDataByState['open'].defaultCommand);
					}}
					{...devBtnsV2Props.deviceButtonV2}
				/>
				<DeviceButtonV2
					id="devicebutton-t0f87yp"
					slot="buttonRight"
					icon={renderDataByState[status?.bolt_state].icon}
					aria-label={status?.bolt_state === 'night_lock'
						? a11yLabels.unlock
						: a11yLabels.lock}
					on:click={(e) => {
						e.stopPropagation();
						handleLock(renderDataByState[status?.bolt_state].command);
					}}
					{...devBtnsV2Props.deviceButtonV2}
				/>
			</DoubleDeviceButtonV2>
		{:else}
			<DoubleDeviceButton
				id="doubledevicebutton-2hjwupo"
				isActive={isLoading}
				{isLoading}
				disabled={isLoading || !hasRights}
				aria-label={a11yLabels.doorControl}
			>
				<DeviceButton
					id="devicebutton-w9mg8oa"
					slot="buttonLeft"
					icon={renderDataByState['open'].defaultIcon}
					aria-label={a11yLabels.open}
					on:click={(e) => {
						e.stopPropagation();
						handleLock(renderDataByState['open'].defaultCommand);
					}}
				/>
				<DeviceButton
					id="devicebutton-t0f87yp"
					slot="buttonRight"
					icon={renderDataByState[status?.bolt_state].icon}
					aria-label={status?.bolt_state === 'night_lock'
						? a11yLabels.unlock
						: a11yLabels.lock}
					on:click={(e) => {
						e.stopPropagation();
						handleLock(renderDataByState[status?.bolt_state].command);
					}}
				/>
			</DoubleDeviceButton>
		{/if}
	{:else}
		<ControlsContainer color="color-0">
			<svelte:fragment slot="first">
				<DeviceButton
					id="devicebutton-z38ho7d"
					icon={renderDataByState['open'].defaultIcon}
					isActive={isLoading && pendingCommand === 'open'}
					isLoading={isLoading && pendingCommand === 'open'}
					disabled={isLoading || !hasRights}
					size="l"
					aria-label={a11yLabels.open}
					on:click={(e) => {
						handleLock(renderDataByState['open'].defaultCommand);
					}}
				/>
				<Text styleType={'controls-large'} class="mt-s text-bold"
					>{renderDataByState['open'].defaultText}</Text
				>
			</svelte:fragment>
			<svelte:fragment slot="second">
				<DeviceButton
					id="devicebutton-ufcow81"
					icon={renderDataByState[status?.bolt_state].icon}
					isActive={isLoading && pendingCommand !== 'open'}
					isLoading={isLoading && pendingCommand !== 'open'}
					disabled={isLoading || !hasRights}
					size="l"
					aria-label={status?.bolt_state === 'night_lock'
						? a11yLabels.unlock
						: a11yLabels.lock}
					on:click={(e) => {
						handleLock(renderDataByState[status?.bolt_state].command);
					}}
				/>
				<Text styleType={'controls-large'} class="mt-s text-bold"
					>{renderDataByState[status?.bolt_state].text}</Text
				>
			</svelte:fragment>
		</ControlsContainer>
	{/if}
{/if}
